import {Loader2} from "lucide-react";
import {useEffect, useState} from "react";

import {Button} from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {Label} from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/sch-select";

import {useUpdateCompanyVehicle} from "../hooks/use-update-company-vehicle";
import {formatCompanyVehicleStatusLabel} from "../lib/format-company-vehicle-status";
import {companyVehicleStatusSchema} from "../schemas";
import type {CompanyVehicle, CompanyVehicleStatus} from "../types";

const STATUS_OPTIONS = companyVehicleStatusSchema.options;

type CompanyVehicleStatusDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicle: CompanyVehicle;
};

export function CompanyVehicleStatusDialog({
  open,
  onOpenChange,
  vehicle,
}: CompanyVehicleStatusDialogProps) {
  const {mutateAsync, isPending} = useUpdateCompanyVehicle();
  const [status, setStatus] = useState<CompanyVehicleStatus>("active");

  useEffect(() => {
    if (open) {
      const s = vehicle.status;
      setStatus(
        s === "inactive" || s === "maintenance" || s === "retired" || s === "active"
          ? s
          : "active",
      );
    }
  }, [open, vehicle]);

  const handleSubmit = async () => {
    await mutateAsync({id: vehicle.id, payload: {status}});
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" showCloseButton>
        <DialogHeader>
          <DialogTitle>Change vehicle status</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2 py-2">
          <Label htmlFor="company-vehicle-status">Status</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as CompanyVehicleStatus)}>
            <SelectTrigger id="company-vehicle-status" className="w-full">
              <SelectValue>{formatCompanyVehicleStatusLabel(status)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {formatCompanyVehicleStatusLabel(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter className="gap-2 sm:justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSubmit()} disabled={isPending}>
            {isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
            Save status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
